<script setup lang="ts">
// Run clock: ACTIVE play time, not wall time (v1.12, per Javi). The
// clock accrues only while the tab is visible, so minimizing the game
// freezes it. Each interval fire credits its real measured delta —
// clamped, because background-throttled timers deliver one late fire
// after resume that would otherwise credit the whole time away.
const { t } = useLocale()
const run = useRunState()
const route = useRoute()

// ⏱️ v6.23 (perf audit hygiene): only the RUN screens credit time. The
// clock is mounted on every route (app.vue's chip cluster), so with a
// run in progress the title, collection and shop were quietly ticking
// too - inflating playtimeMs, the leaderboard tiebreaker, and making
// the persistence heartbeat write the whole run every 2s on an idle
// title screen (the contract persistence.client.ts:98 says it keeps).
// The chip still SHOWS the run's time everywhere; it just doesn't run.
const RUN_ROUTES = new Set(['/draft', '/map', '/encounter'])

let timer: ReturnType<typeof setInterval> | null = null
let last = 0

function credit() {
  const now = performance.now()
  const delta = now - last
  last = now
  if (document.visibilityState !== 'visible' || !appActive.value || !run.value || !RUN_ROUTES.has(route.path)) return
  run.value.playtimeMs += Math.min(delta, 2000)
}

function onVisibility() {
  // Coming back from the background: restart the meter so the away
  // span is never counted.
  last = performance.now()
}

// 📱 M6: the native shell's background counts as away, like a hidden tab.
const { appActive } = useAppLifecycle()
let stopAppState: (() => void) | null = null

onMounted(() => {
  last = performance.now()
  document.addEventListener('visibilitychange', onVisibility)
  stopAppState = onAppStateChange(onVisibility)
  timer = setInterval(credit, 1000)
})
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibility)
  stopAppState?.()
  if (timer) clearInterval(timer)
})

const label = computed(() => {
  const ms = run.value?.playtimeMs
  if (typeof ms !== 'number') return null
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  // v3.43 (per Javi's banner pass): past the first hour the seconds are
  // noise — and a ticking 7-glyph chip is the widest thing in the
  // banner. Long runs read H:MM, short ones keep M:SS.
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
})
</script>

<template>
  <span v-if="label" class="session-clock" data-testid="session-clock" :title="t('clock.timeInRun')">
    <img src="/ui/icon-stopwatch.webp" alt="⏱" class="clock-img" draggable="false">
    <!-- v3.31 (per Javi): Fredoka has no tabular figures, so a plain
      label breathes as thin '1's tick past — and the ribbon banner
      breathes with it. Each character rides its own fixed-width box. -->
    <span v-for="(ch, i) in label" :key="i" class="clock-ch" :class="{ 'clock-colon': ch === ':' }">{{ ch }}</span>
  </span>
</template>

<style scoped>
.session-clock {
  position: fixed;
  top: calc(8px + var(--safe-top));
  right: calc(10px + var(--safe-right));
  /* Below the frozen headers (z 30) — see app-logo-link (v0.64). */
  z-index: 20;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(31, 58, 95, 0.15);
  color: var(--color-ink-soft);
  font-size: 0.72rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  /* The per-glyph boxes must never break across lines (v3.44). */
  white-space: nowrap;
  pointer-events: none;
}
/* Painted badge inline with the label (v2.67). */
.clock-img {
  height: 1.05em;
  width: auto;
  vertical-align: -0.15em;
  margin-right: 0.25em;
}
/* One box per glyph: the clock's width depends only on how many
 * characters it shows, never on WHICH digits are ticking past. */
.clock-ch {
  display: inline-block;
  width: 0.62em;
  text-align: center;
}
.clock-colon {
  width: 0.3em;
}
</style>
